import { toast } from "sonner";
import { create } from "zustand";
import { navigateTo } from "./bridge";
import { useDesktopUi, type AiProvider, type RightTab } from "./ui";

/** Plugin que traz cada chat. O Copilot Chat roda no app desktop (servidor oficial do Copilot). */
export const PROVIDER_PLUGIN: Record<AiProvider, string> = {
  claude: "kompil.claude-agent",
  copilot: "kompil.github-copilot",
};

export const PROVIDER_LABEL: Record<AiProvider, string> = { claude: "Claude", copilot: "GitHub Copilot" };

/** Chats disponíveis agora (plugins instalados e ligados), também para quem está fora do React. */
export const useAiProviders = create<{ installed: AiProvider[] }>(() => ({ installed: [] }));

const isAi = (tab: RightTab | null): tab is AiProvider => tab === "claude" || tab === "copilot";

/** Chat de IA aberto de verdade: o pedido vale só se o plugin dele está instalado. */
export function useOpenChat(): AiProvider | null {
  const side = useDesktopUi((s) => s.side);
  const installed = useAiProviders((s) => s.installed);
  return isAi(side) && installed.includes(side) ? side : null;
}

/** Aba da lateral direita que aparece: Pessoas e Chat só dentro de um projeto; IA com o plugin. */
export function useRightTab(inProject: boolean): RightTab | null {
  const side = useDesktopUi((s) => s.side);
  const installed = useAiProviders((s) => s.installed);
  if (!side) return null;
  if (isAi(side)) return installed.includes(side) ? side : null;
  return inProject ? side : null;
}

/** Abre ou fecha uma aba da lateral direita (Pessoas, Chat do time). */
export function toggleSide(tab: RightTab) {
  const ui = useDesktopUi.getState();
  if (isAi(tab)) return toggleChat(tab);
  ui.set({ side: ui.side === tab ? null : tab });
}

/** Abre (ou fecha) um chat de IA. Sem o plugin, leva à loja com o aviso. */
export function toggleChat(provider?: AiProvider) {
  const ui = useDesktopUi.getState();
  const { installed } = useAiProviders.getState();
  const open = isAi(ui.side) && installed.includes(ui.side) ? ui.side : null;
  const last = isAi(ui.side) ? ui.side : null;
  const target = provider ?? open ?? last ?? installed[0] ?? null;
  if (!target || !installed.includes(target)) {
    toast(
      provider
        ? `Instale o plugin ${PROVIDER_LABEL[provider]} para usar este chat.`
        : "Instale o plugin Claude ou o GitHub Copilot para conversar com a IA.",
      {
        action: { label: "Ver plugins", onClick: () => navigateTo("/plugins?category=ia") },
      },
    );
    return;
  }
  ui.set({ side: open === target ? null : target });
}

/** Abre o chat pedido (sem alternar). */
export function openChat(provider: AiProvider): boolean {
  if (!useAiProviders.getState().installed.includes(provider)) {
    toggleChat(provider);
    return false;
  }
  useDesktopUi.getState().set({ side: provider });
  return true;
}
